import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Trophy, Award, Calendar, Loader2, Flame, Star, Share2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { formatStat } from '@/lib/sportStats';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
};

const Stat = ({ label, value }) => (
  <div className="flex flex-col items-center justify-center bg-white/5 border border-white/10 rounded-lg px-3 py-3 min-w-[80px]">
    <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">{label}</div>
    <div className="text-2xl font-black text-white mt-0.5">{value}</div>
  </div>
);

const HighlightBox = ({ icon: Icon, label, value, sub }) => (
  <div className="bg-white/5 border border-white/10 rounded-lg p-4 flex items-start gap-3">
    <div className="w-9 h-9 rounded-full bg-amber-500/15 flex items-center justify-center shrink-0">
      <Icon className="w-4 h-4 text-amber-400" />
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">{label}</div>
      <div className="text-2xl font-black text-amber-400 mt-0.5 truncate">{value}</div>
      {sub && <div className="text-[11px] text-white/50 truncate mt-0.5">{sub}</div>}
    </div>
  </div>
);

export default function PublicTeamDetail() {
  const { shareToken, teamId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [leaderboardToken, setLeaderboardToken] = useState('');
  // Share button state — declared here (above the early returns) so the
  // hook order stays stable across renders.
  const [shareBusy, setShareBusy] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API_URL}/api/share/${shareToken}/team-stats/${teamId}`),
      fetch(`${API_URL}/api/share/${shareToken}`),
    ])
      .then(async ([t, e]) => {
        if (!t.ok) throw new Error('Team not found');
        const teamData = await t.json();
        const eventData = e.ok ? await e.json() : {};
        setData(teamData);
        setLeaderboardToken(eventData.leaderboard_share_token || '');
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [shareToken, teamId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center text-center px-4">
        <div>
          <Trophy className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400 text-lg">{error || 'Team not found.'}</p>
          <Link to={`/share/${shareToken}`} className="text-amber-400 text-sm hover:underline mt-4 inline-block">
            Back to schedule
          </Link>
        </div>
      </div>
    );
  }

  const { team, games_played, wins, losses, players = [], games_log = [], highlights = {}, event_name, event_id, sport, stat_keys = [], stat_labels = {}, primary_stat } = data;
  // Non-basketball events populate `sport` + `stat_labels` so the roster
  // grid can render sport-appropriate columns (Goals / Assists for soccer,
  // Kills / Aces / Blocks for volleyball, etc.) instead of the basketball
  // PPG / RPG / APG / PER columns.
  const isBasketball = !sport || sport === 'basketball';
  // Every stat the sport defines, headline (primary) stat first.
  const cardStatKeys = !isBasketball
    ? (stat_keys.includes(primary_stat) ? [primary_stat, ...stat_keys.filter(k => k !== primary_stat)] : stat_keys)
    : [];
  const teamLogoUrl = team.logo
    ? (team.logo.startsWith('http') ? team.logo : `${API_URL}/api/files/${team.logo}`)
    : null;
  const teamPhotoUrl = team.team_photo
    ? (team.team_photo.startsWith('http') ? team.team_photo : `${API_URL}/api/files/${team.team_photo}`)
    : null;

  const careerHref = (playerName) => {
    // Career pages are now sport-aware — they render the correct stats for
    // basketball, volleyball, and soccer. Only skip when we don't have a
    // leaderboard token to gate the public view.
    if (!leaderboardToken) return null;
    const params = new URLSearchParams({ team: team.name, name: playerName });
    if (event_id) params.append('event_id', event_id);
    return `/stats/leaderboard/${leaderboardToken}/career?${params.toString()}`;
  };

  // ---------- Share flow ---------------------------------------------------
  // Team pages share with their team logo as the attached image (soccer /
  // volleyball / etc. — matches the primary visual of the page). Falls
  // back to the Schedule Guru logo when the team has no logo uploaded.

  const handleShareTeam = async () => {
    if (shareBusy) return;
    setShareBusy(true);
    const url = window.location.href;
    const title = `${team.name} — Team Page`;
    const caption = event_name ? `${team.name} · ${event_name}` : `${team.name}`;
    try {
      // Native share sheet — link only (no attached image). On mobile
      // this drops the user into WhatsApp / Messages / X with the URL
      // preview; the receiver clicks through to the actual team page.
      if (navigator.share) {
        try {
          await navigator.share({ title, text: caption, url });
          return;
        } catch (err) {
          if (err && err.name === 'AbortError') return;
        }
      }
      // Desktop / no-share fallback — copy the URL and confirm.
      try {
        await navigator.clipboard.writeText(url);
        setShareCopied(true);
        toast.success('Team link copied to clipboard');
        setTimeout(() => setShareCopied(false), 2000);
      } catch {
        toast(`Copy this link: ${url}`, { duration: 8000 });
      }
    } finally {
      setShareBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white overflow-x-hidden">
      {/* Top bar */}
      <header className="bg-[#00247D] border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Link to={`/share/${shareToken}`} className="text-white/70 hover:text-white text-sm flex items-center gap-2" data-testid="team-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to schedule
          </Link>
          <div className="flex items-center gap-3 min-w-0">
            {event_name && (
              <div className="text-xs uppercase tracking-[0.2em] text-white/50 truncate max-w-[40vw] hidden sm:block">
                {event_name}
              </div>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={handleShareTeam}
              disabled={shareBusy}
              data-testid="share-team-btn"
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 border-0 font-semibold shrink-0"
              title="Share this team page"
            >
              {shareCopied ? (
                <><Check className="w-4 h-4 mr-1.5" /> Copied</>
              ) : (
                <><Share2 className="w-4 h-4 mr-1.5" /> Share</>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-b from-[#00247D] to-slate-900 border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            {teamLogoUrl ? (
              <img src={teamLogoUrl} alt={team.name}
                className="w-28 h-28 sm:w-36 sm:h-36 rounded-full object-cover border-4 border-amber-500 shadow-xl bg-white"
                data-testid="team-detail-logo" />
            ) : (
              <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-full flex items-center justify-center border-4 border-amber-500 shadow-xl text-5xl font-black text-amber-400 bg-slate-800">
                {(team.name || '?').charAt(0)}
              </div>
            )}
            <div className="flex-1 text-center sm:text-left min-w-0">
              <p className="text-xs uppercase tracking-[0.3em] text-white/60 font-semibold">Team</p>
              <h1 className="text-4xl sm:text-5xl font-black mt-1 text-amber-400 truncate"
                style={{ fontFamily: 'Barlow Condensed' }} data-testid="team-detail-name">
                {team.name?.toUpperCase()}
              </h1>
              <p className="text-sm text-white/60 mt-2">
                {games_played} game{games_played !== 1 ? 's' : ''} played ·
                <span className="text-emerald-400 ml-1">{wins}W</span>
                <span className="text-rose-400 ml-1">{losses}L</span>
                <span className="text-white/50 ml-2">{players.length} player{players.length !== 1 ? 's' : ''} on roster</span>
              </p>
            </div>
          </div>
          {teamPhotoUrl && (
            <div className="mt-6 rounded-xl overflow-hidden border border-white/10 max-w-2xl mx-auto">
              <img src={teamPhotoUrl} alt={`${team.name} team photo`} className="w-full h-auto" />
            </div>
          )}
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {/* Highlights */}
        {Object.keys(highlights).length > 0 && (
          <section data-testid="team-highlights-section">
            <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-amber-400 mb-3" style={{ fontFamily: 'Barlow Condensed' }}>Team Highlights</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {highlights.best_game && (
                <HighlightBox icon={Flame} label="Highest-Scoring Game"
                  value={highlights.best_game.value}
                  sub={`${highlights.best_game.result} vs ${highlights.best_game.vs} · ${formatDate(highlights.best_game.date)}`} />
              )}
              {highlights.win_streak && (
                <HighlightBox icon={Trophy} label={highlights.win_streak.label}
                  value={`${highlights.win_streak.value} games`} />
              )}
              {highlights.top_scorer && (
                <HighlightBox icon={Star} label="Top Scorer"
                  value={`${highlights.top_scorer.value} ${highlights.top_scorer.unit}`}
                  sub={highlights.top_scorer.name} />
              )}
            </div>
          </section>
        )}

        {/* Roster grid */}
        <section data-testid="team-roster-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-amber-400" style={{ fontFamily: 'Barlow Condensed' }}>Roster &amp; Season Stats</h2>
            <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">{players.length} players</span>
          </div>
          {players.length === 0 ? (
            <Card className="bg-white/5 border-white/10 p-6 text-center">
              <p className="text-sm text-white/50">No roster submitted yet.</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {players.map(p => {
                const headshot = p.headshot_path
                  ? (p.headshot_path.startsWith('http') ? p.headshot_path : `${API_URL}/api/files/${p.headshot_path}`)
                  : null;
                const href = careerHref(p.name);
                const Inner = (
                  <div className="bg-white/5 border border-white/10 hover:border-amber-500/40 rounded-xl p-4 transition-colors flex items-center gap-3 cursor-pointer">
                    {headshot ? (
                      <img src={headshot} alt={p.name} className="w-14 h-14 rounded-full object-cover border-2 border-white/20 shrink-0" />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-slate-800 text-amber-400 flex items-center justify-center font-bold text-xl shrink-0">
                        {p.jersey ? `#${p.jersey}` : (p.name || '?').charAt(0)}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-white truncate">{p.name}</div>
                      <div className="text-[11px] uppercase tracking-wide text-white/50 mt-0.5">
                        {p.jersey && `#${p.jersey} · `}{p.gp || 0} GP
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-xs">
                        {isBasketball ? (
                          <>
                            <span><span className="text-amber-400 font-bold">{p.ppg ?? 0}</span> <span className="text-white/40">PPG</span></span>
                            <span><span className="text-white/80 font-bold">{p.rpg ?? 0}</span> <span className="text-white/40">RPG</span></span>
                            <span><span className="text-white/80 font-bold">{p.apg ?? 0}</span> <span className="text-white/40">APG</span></span>
                            <span><span className="text-emerald-400 font-bold">{p.per ?? 0}</span> <span className="text-white/40">PER</span></span>
                          </>
                        ) : (
                          // Sport-aware chips: show the sport's primary stat
                          // in amber (matches the "Top Scorer" hero style)
                          // and the runner-up in white. GP is already shown
                          // above so no need to repeat it here.
                          cardStatKeys.map((k, idx) => (
                            <span key={k}>
                              <span className={idx === 0 ? 'text-amber-400 font-bold' : 'text-white/80 font-bold'}>
                                {formatStat(k, (p.sport_stats || {})[k] ?? 0)}
                              </span>{' '}
                              <span className="text-white/40 uppercase text-[10px] tracking-wider">
                                {stat_labels[k] || k}
                              </span>
                            </span>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                );
                return href ? (
                  <Link key={p.player_id || p.name} to={href}
                    data-testid={`team-roster-player-link-${p.name}`}>
                    {Inner}
                  </Link>
                ) : (
                  <div key={p.player_id || p.name}>{Inner}</div>
                );
              })}
            </div>
          )}
        </section>

        {/* Game log */}
        {games_log.length > 0 && (
          <section data-testid="team-games-log">
            <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-amber-400 mb-3" style={{ fontFamily: 'Barlow Condensed' }}>Game Results</h2>
            <div className="bg-white/5 border border-white/10 rounded-lg overflow-x-auto">
              <Table className="w-full text-sm">
                <TableHeader>
                  <TableRow className="text-white/50 text-xs uppercase tracking-wide hover:bg-transparent border-white/10">
                    <TableHead className="text-left px-3 py-2 text-white/50">Date</TableHead>
                    <TableHead className="text-left px-3 py-2 text-white/50">Opponent</TableHead>
                    <TableHead className="text-center px-2 py-2 text-white/50">Result</TableHead>
                    <TableHead className="text-right px-3 py-2 text-white/50">Score</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {games_log.map((g, i) => (
                    <TableRow key={g.game_id || i} className="border-t border-white/10 hover:bg-white/5">
                      <TableCell className="px-3 py-2 whitespace-nowrap text-white/70">{formatDate(g.date)}</TableCell>
                      <TableCell className="px-3 py-2 text-white/90">{g.opponent}</TableCell>
                      <TableCell className="text-center px-2 py-2">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${g.result === 'W' ? 'bg-emerald-500/20 text-emerald-300' : g.result === 'L' ? 'bg-rose-500/20 text-rose-300' : 'bg-white/10 text-white/60'}`}>
                          {g.result}
                        </span>
                      </TableCell>
                      <TableCell className="text-right px-3 py-2 font-mono text-white font-bold" data-testid={`team-game-score-${g.game_id}`}>{g.score_line || `${g.my_score}-${g.opp_score}`}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </section>
        )}

        <div className="text-center text-xs text-white/40 pt-6 pb-2 border-t border-white/10">
          <p>Powered by <Link to="/" className="text-amber-400 hover:underline">scheduleguru.ws</Link></p>
        </div>
      </div>
    </div>
  );
}
